import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { DEFAULT_EXPERIMENT_WEEKS } from '../../constants/experiments';
import { getApiErrorMessage } from '../../utils/apiError';
import AppliedPricingDialog from '../shared/AppliedPricingDialog';
import { renderWithAiHighlights } from '../ProblemAnalysisPanel/utils/highlight';
import AlertRow from './AlertRow';
import {
  MachineQuadrant, alternativeOptions, alternativeGroupLabel, AlternativeOptionRow, useReplacementAlternatives,
} from '../shared/ReplacementAlternatives';

const formatNumber = (value, options = {}) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '—';
  }
  return new Intl.NumberFormat(undefined, options).format(Number(value));
};

const formatCompactNumber = (value) => {
  const num = Number(value) || 0;
  const abs = Math.abs(num);
  if (abs >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return `${Math.round(num)}`;
};

const ensureDate = (value, fallback = null) => {
  if (!value) return fallback;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date;
};

const addDays = (date, days) => {
  const clone = new Date(date);
  clone.setDate(clone.getDate() + days);
  return clone;
};

const weeksToDays = (weeks) => weeks * 7;

const CUSTOM_CANDIDATE_VALUE = 'custom-other';

const ProductAIActionPopover = ({
  open,
  anchorEl,
  onClose,
  aiAction,
  productName,
  clientId,
  productId,
  storeId,
  onUpdated
}) => {
  const { t } = useTranslation();
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [discount, setDiscount] = useState('');
  const [status, setStatus] = useState('accepted');
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [priceReview, setPriceReview] = useState(null);
  const [candidateOptions, setCandidateOptions] = useState([]);
  const productsCacheRef = useRef(null);
  const allProductsCacheRef = useRef(null);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [allProductsError, setAllProductsError] = useState('');
  const [customProduct, setCustomProduct] = useState(null);

  // Section collapse states
  const [problemsExpanded, setProblemsExpanded] = useState(true);
  const [alertsExpanded, setAlertsExpanded] = useState(true);

  // Ref for popover actions to trigger repositioning
  const popoverActionsRef = useRef(null);

  // Callback to reposition popover when AlertRow content changes
  const handleAlertContentChange = useCallback(() => {
    // Force popover to recalculate position after content changes
    setTimeout(() => {
      popoverActionsRef.current?.updatePosition();
    }, 50);
  }, []);

  const loadAllProducts = useCallback(async () => {
    if (!clientId) return;

    if (allProductsCacheRef.current) {
      setAllProducts(allProductsCacheRef.current);
      return;
    }

    let token = null;
    try {
      setAllProductsLoading(true);
      setAllProductsError('');

      token = localStorage.getItem('access_token');
      if (!token) {
        setAllProductsError('No se pudo cargar la lista de productos');
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/all-products?clientId=${clientId}`,
        { headers: createAuthHeader(token) }
      );
      const list = Array.isArray(response.data?.products) ? response.data.products : [];
      allProductsCacheRef.current = list;
      setAllProducts(list);
    } catch (loadError) {
      console.error('Error loading full product list:', loadError);
      setAllProductsError('Error al cargar productos');
    } finally {
      setAllProductsLoading(false);
    }
  }, [clientId]);

  const experiment = aiAction?.experiment;
  // Replacements: what else could come in, by margin (the recommendation first) and by volume.
  const replacementAlternatives = useReplacementAlternatives(
    clientId, experiment?.id, Boolean(open && experiment?.type === 'reemplazo_producto'),
  );
  const alternatives = alternativeOptions(replacementAlternatives || {});
  const problem = aiAction?.problem;
  const alerts = aiAction?.alerts || [];

  const hasProblem = Boolean(experiment || problem);
  const hasAlerts = alerts.length > 0;
  const hasBothSections = hasProblem && hasAlerts;

  useEffect(() => {
    if (!experiment) return;
    const firstCandidate =
      Array.isArray(experiment.candidateProductIds) && experiment.candidateProductIds.length > 0
        ? Number(experiment.candidateProductIds[0])
        : null;
    setSelectedCandidate(
      experiment.selectedCandidateId ?? firstCandidate
    );
    setDiscount(
      experiment.type === 'promo_precio_unitario' && experiment.discount !== null && experiment.discount !== undefined
        ? String(experiment.discount)
        : ''
    );
    const normalizedStatus = (() => {
      const value = experiment.status;
      if (!value || value === 'manual_review') return 'accepted';
      return ['accepted', 'pending'].includes(value) ? value : 'accepted';
    })();
    setStatus(normalizedStatus);

    const fallbackStart = new Date();
    fallbackStart.setDate(fallbackStart.getDate() + 10);
    const fallbackEnd = addDays(fallbackStart, weeksToDays(DEFAULT_EXPERIMENT_WEEKS));

    const startDate = fallbackStart;
    const enforcedEnd = addDays(startDate, weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
    setDateFrom(startDate);
    setDateTo(enforcedEnd);
    setError('');
    setCustomProduct(null);
  }, [experiment, open]);

  useEffect(() => {
    if (!experiment) {
      setCandidateOptions([]);
      return;
    }
    const baseCandidates = Array.isArray(experiment.candidateProducts)
      ? experiment.candidateProducts
      : [];
    const limitedCandidates = baseCandidates.slice(0, 3);
    setCandidateOptions(limitedCandidates);

    const needsLookup = limitedCandidates.some(
      (candidate) =>
        !candidate?.name || /^Producto\s+\d+/i.test(candidate.name.trim())
    );
    if (!needsLookup || !clientId) {
      return;
    }

    const loadProductNames = async () => {
      try {
        if (!productsCacheRef.current) {
          const token = localStorage.getItem('access_token');
          if (!token) return;

          const response = await axios.get(
            `${API_BASE_URL}/all-products?clientId=${clientId}`,
            { headers: createAuthHeader(token) }
          );
          const productRows = Array.isArray(response.data?.products)
            ? response.data.products
            : [];
          const productMap = new Map(
            productRows.map((row) => [Number(row.id), row.name || `Producto ${row.id}`])
          );
          productsCacheRef.current = productMap;
        }

        const map = productsCacheRef.current;
        const enriched = limitedCandidates.map((candidate) => {
          const resolvedName =
            map.get(Number(candidate.id)) ??
            candidate.name ??
            `Producto ${candidate.id}`;
          return { ...candidate, name: resolvedName };
        });
        setCandidateOptions(enriched);
      } catch (lookupError) {
        console.error('Error loading candidate product names:', lookupError);
      }
    };

    loadProductNames();
  }, [experiment, clientId]);

  useEffect(() => {
    if (
      selectedCandidate === null &&
      Array.isArray(candidateOptions) &&
      candidateOptions.length > 0
    ) {
      setSelectedCandidate(candidateOptions[0].id);
    }
  }, [candidateOptions, selectedCandidate]);

  useEffect(() => {
    if (selectedCandidate === CUSTOM_CANDIDATE_VALUE) {
      loadAllProducts();
    }
  }, [selectedCandidate, loadAllProducts]);

  useEffect(() => {
    if (selectedCandidate !== CUSTOM_CANDIDATE_VALUE) {
      setCustomProduct(null);
      setAllProductsError('');
    }
  }, [selectedCandidate]);

  const problemInfo = problem?.problemInfo || {};
  const recommendation = useMemo(() => {
    return experiment?.aiRecommendation?.length
      ? experiment.aiRecommendation.join('\n\n')
      : experiment?.contextText || '';
  }, [experiment]);

  const handleSubmit = async (pricingOverride = null) => {
    if (!experiment?.id || !clientId) {
      setError('Faltan datos del experimento');
      return;
    }

    if (!dateFrom || !dateTo) {
      setError('Selecciona fechas de inicio y fin');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const body = {
        experimentType: experiment.type,
        status,
        dateFrom: format(dateFrom, 'yyyy-MM-dd'),
        dateTo: format(dateTo, 'yyyy-MM-dd'),
        innovation: false
      };

      if (experiment.type === 'promo_precio_unitario') {
        const numericDiscount = Number(discount || 0);
        if (Number.isNaN(numericDiscount)) {
          setError('Descuento inválido');
          setSubmitting(false);
          return;
        }
        body.discount = numericDiscount;
      } else {
        let candidateProductId = null;
        if (selectedCandidate === CUSTOM_CANDIDATE_VALUE) {
          if (!customProduct?.id) {
            setError('Selecciona un producto alternativo');
            setSubmitting(false);
            return;
          }
          candidateProductId = Number(customProduct.id);
        } else if (selectedCandidate !== null && selectedCandidate !== undefined) {
          candidateProductId = Number(selectedCandidate);
        } else if (experiment.newProductId) {
          candidateProductId = Number(experiment.newProductId);
        }

        if (!candidateProductId || Number.isNaN(candidateProductId)) {
          setError('Selecciona un producto válido');
          setSubmitting(false);
          return;
        }

        body.candidateProductId = candidateProductId;
        body.discount = 0;
      }

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Token no encontrado');
      }
      if (!pricingOverride && status === 'accepted') {
        const preview = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${clientId}`,
          { experimentId: experiment.id, ...body },
          { headers: createAuthHeader(token) }
        );
        if (preview.data?.pricing) { setPriceReview(preview.data.pricing); return; }
      }
      if (pricingOverride) body.pricingOverride = pricingOverride;

      const response = await axios.post(
        `${API_BASE_URL}/experiments/${experiment.id}/ai-override?clientId=${clientId}`,
        body,
        { headers: createAuthHeader(token) }
      );

      // Sin confirmación de lo que aplicó el ERP: el operador ya vio y aprobó esas mismas
      // tarifas en la vista previa, repetirlas después sólo añade un clic.
      onUpdated?.();
      onClose();
    } catch (submitError) {
      console.error('Error updating experiment:', submitError);
      setError(getApiErrorMessage(submitError, 'Error guardando experimento'));
    } finally {
      setSubmitting(false);
    }
  };

  const getMetricSymbol = (icon) => {
    switch (icon) {
      case 'arrow_down':
        return '↓';
      case 'triangle_up':
        return '△';
      case 'half_circle':
        return '◐';
      case 'diamond':
        return '◆';
      default:
        return '●';
    }
  };

  const getMetricColor = (status) => {
    switch (status) {
      case 'danger':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'good':
        return '#4caf50';
      default:
        return '#6b7280';
    }
  };

  const renderMetrics = () => {
    const contextMetrics = problem?.context?.metrics;

    if (contextMetrics && contextMetrics.length > 0) {
      return contextMetrics.slice(0, 3).map((metric, index) => {
        const symbol = getMetricSymbol(metric.icon);
        const color = getMetricColor(metric.status);
        const mainText = `${formatCompactNumber(metric.value)} unidades${
          metric.detail?.category ? ` en ${metric.detail.category}` : ''
        }${metric.detail?.sector ? ` (${metric.detail.sector})` : ''}`;
        let secondaryText =
          metric.detail?.description ||
          metric.detail?.period ||
          metric.detail?.location ||
          '';
        if (!secondaryText?.includes('6 semana')) {
          secondaryText = secondaryText
            ? `${secondaryText} (6 semanas)`
            : '(6 semanas)';
        }

        return (
          <Box
            key={`metric-${index}`}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                color,
                minWidth: '16px',
                textAlign: 'center',
                lineHeight: 1.2
              }}
            >
              {symbol}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'text.primary',
                  lineHeight: 1.3
                }}
              >
                {mainText}
              </Typography>
              {secondaryText && (
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: 'text.secondary',
                    lineHeight: 1.2
                  }}
                >
                  {secondaryText}
                </Typography>
              )}
            </Box>
          </Box>
        );
      });
    }

    const fallback = [
      {
        primary: `${formatNumber(problemInfo.units_6wk || problemInfo.units_4wk)} unidades`,
        secondary: 'Rendimiento del producto (6 semanas)'
      },
      {
        primary: `${formatNumber(problemInfo.category_sector_median)} unidades`,
        secondary: 'Promedio de la categoría en el sector (6 semanas)'
      },
      {
        primary: `${formatNumber(problemInfo.cluster_total_units)} unidades`,
        secondary: 'Total de la ubicación en el periodo (6 semanas)'
      }
    ];

    return fallback.map((item, index) => (
      <Box
        key={`fallback-metric-${index}`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: '#6b7280',
            minWidth: '16px',
            textAlign: 'center',
            lineHeight: 1.2
          }}
        >
          •
        </Typography>
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              color: 'text.primary',
              lineHeight: 1.3
            }}
          >
            {item.primary}
          </Typography>
          <Typography
            sx={{
              fontSize: '11px',
              color: 'text.secondary',
              lineHeight: 1.2
            }}
          >
            {item.secondary}
          </Typography>
        </Box>
      </Box>
    ));
  };

  return (
    <>
    <AppliedPricingDialog open={Boolean(priceReview)} pricing={priceReview} onClose={() => setPriceReview(null)} onConfirm={(pricingOverride) => { setPriceReview(null); handleSubmit(pricingOverride); }} />
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      action={popoverActionsRef}
      anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      marginThreshold={16}
      PaperProps={{
        sx: {
          width: 380,
          maxHeight: 'calc(100vh - 32px)',
          overflow: 'auto',
          p: 2,
          borderRadius: '12px'
        }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {/* Product name header */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {productName || t('product', { defaultValue: 'Producto' })}
        </Typography>

        {/* PROBLEMS SECTION */}
        {hasProblem && (
          <>
            {/* Section header - collapsible when alerts also exist */}
            {hasBothSections && (
              <Box
                onClick={() => setProblemsExpanded(!problemsExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: 0.5,
                  px: 1,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(99, 102, 241, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.12)' }
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#6366f1' }}>
                  {t('problemsSection', { defaultValue: 'Problema de Rendimiento' })}
                </Typography>
                <IconButton size="small" sx={{ p: 0 }}>
                  {problemsExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            )}

            <Collapse in={!hasBothSections || problemsExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Metrics */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {renderMetrics()}
                </Box>

                {/* AI Recommendation */}
                {recommendation && (
                  <Alert severity="info" sx={{ fontSize: '12px', lineHeight: 1.4 }}>
                    {renderWithAiHighlights(recommendation)}
                  </Alert>
                )}

                <Divider />

                {/* Date pickers */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(2, 1fr)' }}>
                    <DatePicker
                      label={t('startDate', { defaultValue: 'Inicio' })}
                      value={dateFrom}
                      onChange={(value) => {
                        const next = ensureDate(value, null);
                        if (!next) {
                          setDateFrom(value);
                          return;
                        }
                        const normalizedStart = new Date(next);
                        const adjustedEnd = addDays(normalizedStart, weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
                        setDateFrom(normalizedStart);
                        setDateTo(adjustedEnd);
                      }}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                    <DatePicker
                      label={t('endDate', { defaultValue: 'Fin' })}
                      value={dateTo}
                      onChange={(value) => {
                        const next = ensureDate(value, null);
                        if (!next) {
                          setDateTo(value);
                          return;
                        }
                        const normalizedEnd = new Date(next);
                        const adjustedStart = addDays(normalizedEnd, -weeksToDays(DEFAULT_EXPERIMENT_WEEKS));
                        setDateFrom(adjustedStart);
                        setDateTo(normalizedEnd);
                      }}
                      slotProps={{ textField: { size: 'small' } }}
                    />
                  </Box>
                </LocalizationProvider>

                {/* Discount/Replacement fields */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {experiment?.type === 'promo_precio_unitario' ? (
                    <TextField
                      label={t('discount', { defaultValue: 'Descuento' }) + ' (%)'}
                      size="small"
                      value={discount}
                      onChange={(event) => setDiscount(event.target.value)}
                    />
                  ) : (
                    <>
                    <MachineQuadrant quadrant={replacementAlternatives?.quadrant} />
                    <FormControl size="small">
                      <InputLabel id="candidate-product-label">{t('replacement', { defaultValue: 'Reemplazo' })}</InputLabel>
                      <Select
                        labelId="candidate-product-label"
                        label={t('replacement', { defaultValue: 'Reemplazo' })}
                        value={selectedCandidate ?? ''}
                        // A product can be in both groups (same value): closed, it shows its name, as before,
                        // not the figure of whichever row matched last.
                        renderValue={alternatives.length > 0 ? (value) => (
                          value === CUSTOM_CANDIDATE_VALUE
                            ? t('selectOtherProduct', { defaultValue: 'Selecciona otro producto' })
                            : alternatives.find((a) => a.productId === Number(value))?.productName
                              ?? (candidateOptions || []).find((c) => Number(c.id) === Number(value))?.name
                              ?? value
                        ) : undefined}
                        onChange={(event) => {
                          const value = event.target.value;
                          setError('');
                          if (value === CUSTOM_CANDIDATE_VALUE) {
                            setSelectedCandidate(CUSTOM_CANDIDATE_VALUE);
                            return;
                          }
                          const numericValue =
                            typeof value === 'string' ? parseInt(value, 10) : value;
                          setSelectedCandidate(Number.isNaN(numericValue) ? null : numericValue);
                        }}
                      >
                        {(candidateOptions || [])
                          .filter((candidate) => !alternatives.some((a) => a.productId === Number(candidate.id)))
                          .map((candidate) => (
                            <MenuItem key={candidate.id} value={candidate.id}>
                              {candidate.name}
                            </MenuItem>
                          ))}
                        {['margin', 'volume'].flatMap((group) => {
                          const items = alternatives.filter((a) => a.group === group);
                          if (items.length === 0) return [];
                          return [
                            <ListSubheader key={`header-${group}`} sx={{ fontSize: '11px', lineHeight: '28px' }}>
                              {alternativeGroupLabel(t, group)}
                            </ListSubheader>,
                            ...items.map((option) => (
                              <MenuItem key={option.key} value={option.productId}>
                                <AlternativeOptionRow option={option} />
                              </MenuItem>
                            )),
                          ];
                        })}
                        <MenuItem value={CUSTOM_CANDIDATE_VALUE}>
                          {t('selectOtherProduct', { defaultValue: 'Selecciona otro producto' })}
                        </MenuItem>
                      </Select>
                      {selectedCandidate === CUSTOM_CANDIDATE_VALUE && (
                        <Box sx={{ mt: 1 }}>
                          <Autocomplete
                            value={customProduct}
                            onChange={(_, newValue) => {
                              setCustomProduct(newValue);
                              setError('');
                            }}
                            options={allProducts}
                            loading={allProductsLoading}
                            getOptionLabel={(option) => option?.name || ''}
                            isOptionEqualToValue={(option, value) =>
                              option?.id === value?.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('selectProduct', { defaultValue: 'Selecciona producto' })}
                                size="small"
                                placeholder={t('searchProduct', { defaultValue: 'Buscar producto...' })}
                                error={Boolean(allProductsError)}
                                helperText={allProductsError || undefined}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {allProductsLoading ? (
                                        <CircularProgress color="inherit" size={16} />
                                      ) : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  )
                                }}
                              />
                            )}
                          />
                        </Box>
                      )}
                    </FormControl>
                    </>
                  )}

                  {/* Status dropdown */}
                  <FormControl size="small">
                    <InputLabel id="experiment-status-label">{t('status', { defaultValue: 'Estado' })}</InputLabel>
                    <Select
                      labelId="experiment-status-label"
                      label={t('status', { defaultValue: 'Estado' })}
                      value={status}
                      onChange={(event) => setStatus(event.target.value)}
                    >
                      <MenuItem value="accepted">{t('accepted', { defaultValue: 'Aceptado' })}</MenuItem>
                      <MenuItem value="pending">{t('pending', { defaultValue: 'Pendiente' })}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Error message */}
                {error && (
                  <Alert severity="error" sx={{ fontSize: '12px' }}>
                    {error}
                  </Alert>
                )}

                {/* Save button for problem */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  <Button
                    variant="contained"
                    size="small"
                    // Sin envolver, React pasa el evento del click como `pricingOverride`: al ser
                    // truthy se saltaba la vista previa de precios y el evento acababa en el body,
                    // donde JSON.stringify revienta con "cyclic object value".
                    onClick={() => handleSubmit()}
                    disabled={submitting}
                  >
                    {submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : t('save', { defaultValue: 'Guardar' })}
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </>
        )}

        {/* Divider between sections */}
        {hasBothSections && <Divider sx={{ my: 0.5 }} />}

        {/* ALERTS SECTION */}
        {hasAlerts && (
          <>
            {/* Section header - collapsible when problems also exist */}
            {hasBothSections && (
              <Box
                onClick={() => setAlertsExpanded(!alertsExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  py: 0.5,
                  px: 1,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(168, 85, 247, 0.08)',
                  '&:hover': { backgroundColor: 'rgba(168, 85, 247, 0.12)' }
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#a855f7' }}>
                  {t('alertsSection', { defaultValue: 'Alertas Técnicas' })} ({alerts.length})
                </Typography>
                <IconButton size="small" sx={{ p: 0, color: '#a855f7' }}>
                  {alertsExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                </IconButton>
              </Box>
            )}

            {/* Section header when no problems - styled like the collapsible version */}
            {!hasBothSections && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 0.5,
                  px: 1,
                  borderRadius: '6px',
                  backgroundColor: 'rgba(168, 85, 247, 0.08)',
                }}
              >
                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#a855f7' }}>
                  {t('alertsSection', { defaultValue: 'Alertas Técnicas' })} ({alerts.length})
                </Typography>
              </Box>
            )}

            <Collapse in={!hasBothSections || alertsExpanded}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {alerts.map((alert) => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    clientId={clientId}
                    storeId={storeId}
                    productId={productId}
                    productName={productName}
                    onAlertUpdated={onUpdated}
                    onContentChange={handleAlertContentChange}
                  />
                ))}
              </Box>
            </Collapse>
          </>
        )}

        {/* Close button - always visible */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button onClick={onClose} size="small">
            {t('close', { defaultValue: 'Cerrar' })}
          </Button>
        </Box>
      </Box>
    </Popover>
    </>
  );
};

export default ProductAIActionPopover;
